// @client/components/profile-manager/ProfileToolbar.tsx
// V2.0 - Multi-Select für Lists und Categories (ohne cmdk)
import React, { useState, useMemo } from "react";
import { 
  Download, Factory, Trash2, Contact, Tag, List, Settings, 
  Mail, Users, MoreVertical, Check, X, Search, ChevronsUpDown 
} from "lucide-react";
import { Button } from "@client/components/ui/button";
import { Badge } from "@client/components/ui/badge";
import { Input } from "@client/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@client/components/ui/popover";
import { ScrollArea } from "@client/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal
} from "@client/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@client/components/ui/alert-dialog";

// Import Core-Types direkt via @shared/types
import type { 
  Category, 
  List as ListType,
  BulkScope 
} from "@shared/types";

export interface ProfileToolbarProps {
  bulkScope: BulkScope;
  lists: ListType[];
  categories: Category[];
  // ✅ V2.0: Multi-Select Callbacks
  onCategoriesUpdate: (categoryIds: number[]) => void;
  onListsUpdate: (listIds: number[]) => void;
  // Legacy single-select (optional, for backwards compatibility)
  onCategoryUpdate?: (categoryId: number) => void;
  onListUpdate?: (listId: number) => void;
  onMarkerUpdate: (marker: string, value: boolean) => void;
  onEditFields: () => void;
  onAction: (action: string) => void;
  loading?: boolean;
  disabled?: boolean;
}

export function ProfileToolbar({
  bulkScope,
  lists,
  categories,
  onCategoriesUpdate,
  onListsUpdate,
  onCategoryUpdate,
  onListUpdate,
  onMarkerUpdate,
  onEditFields,
  onAction,
  loading,
  disabled
}: ProfileToolbarProps) {

  // ==========================================
  // STATE
  // ==========================================
  const [confirmingAction, setConfirmingAction] = useState<string | null>(null);
  
  // Multi-Select State
  const [selectedListIds, setSelectedListIds] = useState<number[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  
  // Popover State
  const [listPopoverOpen, setListPopoverOpen] = useState(false);
  const [categoryPopoverOpen, setCategoryPopoverOpen] = useState(false);
  
  // Search State
  const [listSearch, setListSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");

  // ==========================================
  // FILTERED DATA
  // ==========================================
  const filteredLists = useMemo(() => {
    if (!listSearch.trim()) return lists;
    const search = listSearch.toLowerCase();
    return lists.filter(list => 
      list.name.toLowerCase().includes(search) ||
      (list.description && list.description.toLowerCase().includes(search))
    );
  }, [lists, listSearch]);

  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return categories;
    const search = categorySearch.toLowerCase();
    return categories.filter(cat => 
      cat.name.toLowerCase().includes(search) ||
      (cat.description && cat.description.toLowerCase().includes(search))
    );
  }, [categories, categorySearch]);

  // ==========================================
  // MULTI-SELECT HANDLERS
  // ==========================================
  const handleListToggle = (listId: number) => {
    setSelectedListIds(prev => 
      prev.includes(listId) 
        ? prev.filter(id => id !== listId)
        : [...prev, listId]
    );
  };

  const handleCategoryToggle = (categoryId: number) => {
    setSelectedCategoryIds(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleApplyLists = () => {
    if (selectedListIds.length === 0 || disabled) return;
    
    const selectedNames = lists
      .filter(l => selectedListIds.includes(l.id))
      .map(l => l.name)
      .join(", ");
    
    const confirmed = window.confirm(
      `Add ${bulkScope.label} to ${selectedListIds.length} list(s)?\n\nLists: ${selectedNames}\n\nThis action cannot be undone.`
    );
    
    if (confirmed) {
      onListsUpdate(selectedListIds);
      setSelectedListIds([]);
      setListPopoverOpen(false);
    }
  };

  const handleApplyCategories = () => {
    if (selectedCategoryIds.length === 0 || disabled) return;
    
    const selectedNames = categories
      .filter(c => selectedCategoryIds.includes(c.id))
      .map(c => c.name)
      .join(", ");
    
    const confirmed = window.confirm(
      `Add ${selectedCategoryIds.length} category/categories to ${bulkScope.label}?\n\nCategories: ${selectedNames}\n\nThis action cannot be undone.`
    );
    
    if (confirmed) {
      onCategoriesUpdate(selectedCategoryIds);
      setSelectedCategoryIds([]);
      setCategoryPopoverOpen(false);
    }
  };

  const handleClearLists = () => {
    setSelectedListIds([]);
  };

  const handleClearCategories = () => {
    setSelectedCategoryIds([]);
  };

  // ==========================================
  // OTHER HANDLERS
  // ==========================================
  const handleMarkerUpdate = (marker: string, value: boolean, label: string) => {
    if (disabled) return;
    
    const confirmed = window.confirm(
      `Set "${label}" to ${value ? 'Yes' : 'No'} for ${bulkScope.label}?\n\nThis action cannot be undone.`
    );
    
    if (confirmed) {
      onMarkerUpdate(marker, value);
    }
  };

  const handleActionClick = (action: string) => {
    if (disabled) return;
    setConfirmingAction(action);
  };

  const executeAction = (action: string) => {
    onAction(action);
    setConfirmingAction(null);
  };

  // ==========================================
  // HELPER FUNCTIONS
  // ==========================================
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'export': return <Download className="h-4 w-4" />;
      case 'delete': return <Trash2 className="h-4 w-4" />;
      case 'generate-email': return <Mail className="h-4 w-4" />;
      case 'generate-gender': return <Users className="h-4 w-4" />;
      case 'generate-industry': return <Factory className="h-4 w-4" />;
      case 'check-crmid': return <Contact className="h-4 w-4" />;
      default: return <MoreVertical className="h-4 w-4" />;
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'export': return 'Export CSV';
      case 'delete': return 'Delete Profiles';
      case 'generate-email': return 'Generate Emails';
      case 'generate-industry': return 'Generate Industry';
      case 'generate-gender': return 'Generate Gender';
      case 'check-crmid': return 'Check CRMWeb';
      default: return action;
    }
  };

  const getActionDescription = (action: string) => {
    switch (action) {
      case 'export': 
        return `Export ${bulkScope.label} as CSV file for download.`;
      case 'delete': 
        return `Permanently delete ${bulkScope.label}. This action cannot be undone.`;
      case 'generate-email': 
        return `Generate email addresses for ${bulkScope.label} using pattern matching.`;
      case 'generate-industry': 
        return `Determine industry for ${bulkScope.label} based on company stem.`;
      case 'generate-gender': 
        return `Determine gender for ${bulkScope.label} based on first names.`;
      case 'check-crmid': 
        return `Check data for existing entry in CRMweb.`;
      default: 
        return `Execute ${action} for ${bulkScope.label}.`;
    }
  };

  const isDestructiveAction = (action: string) => {
    return action === 'delete';
  };

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Scope Indicator */}
      <div className="flex items-center gap-2 mr-4">
        <Badge variant="outline" className="font-normal">
          {bulkScope.label}
        </Badge>
        {disabled && (
          <span className="text-xs text-muted-foreground">
            No profiles to process
          </span>
        )}
      </div>

      {/* ==========================================
          CATEGORY MULTI-SELECT POPOVER
          ========================================== */}
      <Popover open={categoryPopoverOpen} onOpenChange={setCategoryPopoverOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            disabled={loading || disabled}
            className="justify-between min-w-[120px]"
          >
            <Tag className="h-4 w-4 mr-2" />
            {selectedCategoryIds.length === 0 
              ? "Category" 
              : `${selectedCategoryIds.length} selected`}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <div className="p-2 border-b">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search categories..."
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                className="h-8 border-0 focus-visible:ring-0 p-0"
              />
            </div>
          </div>
          
          <ScrollArea className="h-[200px]">
            <div className="p-2">
              {filteredCategories.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-4">
                  No categories found
                </div>
              ) : (
                filteredCategories.map(category => {
                  const isSelected = selectedCategoryIds.includes(category.id);
                  return (
                    <div
                      key={category.id}
                      onClick={() => handleCategoryToggle(category.id)}
                      className="flex items-center gap-2 p-2 rounded-sm hover:bg-accent cursor-pointer"
                    >
                      <div className={`flex h-4 w-4 items-center justify-center rounded border ${
                        isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                      }`}>
                        {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      {category.color && (
                        <div 
                          className="w-3 h-3 rounded-full border"
                          style={{ backgroundColor: category.color }}
                        />
                      )}
                      <span className="flex-1 truncate text-sm">{category.name}</span>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
          
          {/* Apply/Clear Buttons */}
          <div className="p-2 border-t flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearCategories}
              disabled={selectedCategoryIds.length === 0}
              className="text-xs"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={handleApplyCategories}
              disabled={selectedCategoryIds.length === 0}
              className="text-xs"
            >
              Add {selectedCategoryIds.length > 0 && `(${selectedCategoryIds.length})`}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* ==========================================
          LIST MULTI-SELECT POPOVER
          ========================================== */}
      <Popover open={listPopoverOpen} onOpenChange={setListPopoverOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            disabled={loading || disabled}
            className="justify-between min-w-[100px]"
          >
            <List className="h-4 w-4 mr-2" />
            {selectedListIds.length === 0 
              ? "List" 
              : `${selectedListIds.length} selected`}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <div className="p-2 border-b">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search lists..."
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                className="h-8 border-0 focus-visible:ring-0 p-0"
              />
            </div>
          </div>
          
          <ScrollArea className="h-[200px]">
            <div className="p-2">
              {filteredLists.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-4">
                  No lists found
                </div>
              ) : (
                filteredLists.map(list => {
                  const isSelected = selectedListIds.includes(list.id);
                  return (
                    <div
                      key={list.id}
                      onClick={() => handleListToggle(list.id)}
                      className="flex items-center gap-2 p-2 rounded-sm hover:bg-accent cursor-pointer"
                    >
                      <div className={`flex h-4 w-4 items-center justify-center rounded border ${
                        isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                      }`}>
                        {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="truncate text-sm block">{list.name}</span>
                        {list.description && (
                          <span className="text-xs text-muted-foreground truncate block">
                            {list.description}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
          
          {/* Apply/Clear Buttons */}
          <div className="p-2 border-t flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearLists}
              disabled={selectedListIds.length === 0}
              className="text-xs"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={handleApplyLists}
              disabled={selectedListIds.length === 0}
              className="text-xs"
            >
              Add to {selectedListIds.length > 0 && `(${selectedListIds.length})`}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Markers Dropdown (unchanged) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            disabled={loading || disabled}
          >
            <Settings className="h-4 w-4 mr-2" />
            Markers
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuLabel>Set Markers</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Is Exported</DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem 
                  onClick={() => handleMarkerUpdate('isExported', true, 'Is Exported')}
                >
                  Set to Yes
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleMarkerUpdate('isExported', false, 'Is Exported')}
                >
                  Set to No
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Is Active</DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem 
                  onClick={() => handleMarkerUpdate('isActive', true, 'Is Active')}
                >
                  Set to Yes
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleMarkerUpdate('isActive', false, 'Is Active')}
                >
                  Set to No
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Is Blocked</DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem 
                  onClick={() => handleMarkerUpdate('isBlocked', true, 'Is Blocked')}
                >
                  Set to Yes
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleMarkerUpdate('isBlocked', false, 'Is Blocked')}
                >
                  Set to No
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Is Registered</DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem 
                  onClick={() => handleMarkerUpdate('isRegistered', true, 'Is Registered')}
                >
                  Set to Yes
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleMarkerUpdate('isRegistered', false, 'Is Registered')}
                >
                  Set to No
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit Fields Button */}
      <Button 
        variant="outline" 
        size="sm"
        onClick={onEditFields}
        disabled={loading || disabled}
      >
        Edit Fields...
      </Button>

      {/* Actions Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            disabled={loading || disabled}
          >
            <MoreVertical className="h-4 w-4 mr-2" />
            Actions
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Bulk Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={() => handleActionClick('export')}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={() => handleActionClick('generate-email')}>
            <Mail className="h-4 w-4 mr-2" />
            Generate Emails
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => handleActionClick('generate-gender')}>
            <Users className="h-4 w-4 mr-2" />
            Generate Gender
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => handleActionClick('generate-industry')}>
            <Factory className="h-4 w-4 mr-2" />
            Generate Industry
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => handleActionClick('check-crmid')}>
            <Contact className="h-4 w-4 mr-2" />
            Check CRMWeb
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            onClick={() => handleActionClick('delete')}
            className="text-red-600 focus:text-red-600"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Profiles
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmingAction} onOpenChange={() => setConfirmingAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {confirmingAction && getActionIcon(confirmingAction)}
              Confirm {confirmingAction && getActionLabel(confirmingAction)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmingAction && getActionDescription(confirmingAction)}
              <br /><br />
              <strong>This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmingAction(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmingAction && executeAction(confirmingAction)}
              className={isDestructiveAction(confirmingAction || '') ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {confirmingAction && getActionLabel(confirmingAction)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Loading Indicator */}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
          Processing...
        </div>
      )}
    </div>
  );
}